export type FDAMode =
  | 'none'
  | 'fdaLegacy'
  | 'fdaLegacyUnaided'
  | 'aided'
  | 'unaided';

export type FDAEnvironment = {
  mode: FDAMode;
  // FDA UI is enabled by legacy Annotator or by the standalone FDA 2.0 flag.
  isFDA: boolean;
  // FDA && Unaided — equals the historical `isFDANonAided` semantics
  isUnaided: boolean;
  // Standalone "FDA 2.0" capability
  isFDAVersion2: boolean;
  // Effective FDA 3D GP restrictions flag (consumers still gate it by report type themselves)
  isFDA3DGPRestrictions: boolean;
  predictsVisible: boolean;
  masksVisible: boolean;
  // Manual confidence-% entry mode
  manualPercentInput: boolean;
  // Periapical radiolucency condition button in the legacy tooth card, shown for 3D GP restrictions
  periapicalConditionVisible: boolean;
  // Legacy Annotator periapical-confidence slider, shown only for Annotator + 3D GP restrictions
  confidenceSliderVisible: boolean;
  // Unaided delivers every tooth-like object as Tooth, so the chart treats them as such
  treatToothLikeAsTooth: boolean;
};

type FDAEnvironmentFlags = {
  isFDA3DGPRestrictions: boolean;
  isFDAVersion2: boolean;
  isFDAAnnotator: boolean;
  isFDAUnaided: boolean;
};

const getMode = (
  isFDA: boolean,
  isVersion2: boolean,
  isUnaided: boolean,
): FDAMode => {
  if (!isFDA) {
    return 'none';
  }

  if (isVersion2) {
    return isUnaided ? 'unaided' : 'aided';
  }

  return isUnaided ? 'fdaLegacyUnaided' : 'fdaLegacy';
};

export const deriveFDAEnvironment = (
  flags: FDAEnvironmentFlags,
): FDAEnvironment => {
  const {
    isFDA3DGPRestrictions,
    isFDAVersion2: version2Flag,
    isFDAAnnotator,
    isFDAUnaided,
  } = flags;

  const isFDAVersion2 = version2Flag;
  const isFDA = isFDAAnnotator || isFDAVersion2;
  const isUnaided = isFDA && isFDAUnaided;
  const effectiveFDA3DGPRestrictions = isFDA3DGPRestrictions || isFDAVersion2;

  return {
    mode: getMode(isFDA, isFDAVersion2, isUnaided),
    isFDA,
    isUnaided,
    isFDAVersion2,
    isFDA3DGPRestrictions: effectiveFDA3DGPRestrictions,
    predictsVisible: !isUnaided,
    masksVisible: !isUnaided,
    manualPercentInput: isFDAVersion2,
    periapicalConditionVisible: isFDA3DGPRestrictions && !isFDAVersion2,
    confidenceSliderVisible:
      isFDAAnnotator && isFDA3DGPRestrictions && !isFDAVersion2,
    treatToothLikeAsTooth: isUnaided,
  };
};
